import {
  categoryOptions,
  certaintyOptions,
  eventCodeOptions,
  NOT_SPECIFIED_RESPONSE_TYPE,
  notSpecifiedResponseTypeOption,
  responseTypeOptions,
  severityOptions,
  urgencyOptions,
  WEA_PRIORITY_VALUES,
  WEA_RESPONSE_TYPE_VALUES,
} from '../../config/Ipaws.config';
import IpawsDeliveryChannelsField from '../IpawsDeliveryChannelsField/IpawsDeliveryChannelsField';
import IpawsSelectField from '../IpawsSelectField/IpawsSelectField';
import IpawsTextField from '../IpawsTextField/IpawsTextField';

const getOptionValues = (options) => (options ?? []).map((option) => option.value);

const formatCategoryOption = (option, { context }) =>
  context === 'menu' && option.description ? (
    <span className="d-flex flex-column">
      <span>{option.label}</span>
      <small className="text-secondary">{option.description}</small>
    </span>
  ) : (
    option.label
  );

const formatEventCodeOption = (option, { context }) =>
  context === 'menu' && option.disabledReason ? (
    <span className="d-flex flex-column">
      <span>{option.label}</span>
      <small className="text-secondary">{option.disabledReason}</small>
    </span>
  ) : (
    option.label
  );

const IpawsEventClassificationSection = ({
  draft,
  findings,
  contractOptions,
  environment,
  authorizedWeaHandlingOptions,
  isWeaHandlingLoading = false,
  isWeaHandlingError = false,
  channelRules,
  onDraftChange,
  onTextChange,
}) => {
  const controlledOptions = {
    category: contractOptions?.category ?? categoryOptions,
    responseType: contractOptions?.responseType ?? responseTypeOptions,
    urgency: contractOptions?.urgency ?? urgencyOptions,
    severity: contractOptions?.severity ?? severityOptions,
    certainty: contractOptions?.certainty ?? certaintyOptions,
  };
  const hasWea = draft.deliveryChannels?.includes('CMAS');
  const cogWeaEventCodes = channelRules?.cogEventCodes?.CMAS;
  const channelWeaEventCodes = channelRules?.channelEventCodes?.CMAS;
  const availableEventCodeOptions = hasWea
    ? eventCodeOptions.map((option) => {
        const isSupportedByCog =
          !Array.isArray(cogWeaEventCodes) || cogWeaEventCodes.includes(option.value);
        const isSupportedByFema =
          !Array.isArray(channelWeaEventCodes) || channelWeaEventCodes.includes(option.value);
        const isLocallyUnavailable = ['ADR', 'AVA'].includes(option.value);

        let disabledReason;
        if (isLocallyUnavailable || !isSupportedByFema) disabledReason = 'Not compatible with WEA';
        else if (!isSupportedByCog) disabledReason = 'Not authorized for WEA by this COG';

        return disabledReason ? { ...option, disabledReason } : option;
      })
    : eventCodeOptions;
  const getPriorityOptions = (fieldPath) =>
    hasWea
      ? controlledOptions[fieldPath].filter((option) =>
          WEA_PRIORITY_VALUES[fieldPath].includes(option.value),
        )
      : controlledOptions[fieldPath];
  const priorityFields = [
    ['urgency', 'Urgency', getPriorityOptions('urgency')],
    ['severity', 'Severity', getPriorityOptions('severity')],
    ['certainty', 'Certainty', getPriorityOptions('certainty')],
  ];
  const recommendedPublicActionOptions = [
    notSpecifiedResponseTypeOption,
    ...controlledOptions.responseType,
  ];
  const availableResponseTypeOptions = hasWea
    ? recommendedPublicActionOptions.filter((option) =>
        WEA_RESPONSE_TYPE_VALUES.includes(option.value),
      )
    : recommendedPublicActionOptions;

  const handleResponseTypeChange = (options, actionMeta) => {
    if (actionMeta?.option?.value === NOT_SPECIFIED_RESPONSE_TYPE) {
      onDraftChange({ responseType: [NOT_SPECIFIED_RESPONSE_TYPE] });
      return;
    }

    onDraftChange({
      responseType: getOptionValues(options).filter(
        (responseType) => responseType !== NOT_SPECIFIED_RESPONSE_TYPE,
      ),
    });
  };

  const handleEventCodeChange = (option) => {
    onDraftChange({ eventCode: option?.value ?? '' });
  };

  return (
    <fieldset className="alert-creator-section alert-creator-section-ipaws ipaws-event-classification border rounded-3 p-3 shadow-sm">
      <legend className="alert-creator-section-title d-flex align-items-center">
        Event classification
      </legend>

      <IpawsSelectField
        inputId="ipaws-alert-event-code"
        fieldPath="eventCode"
        label="SAME event code *"
        options={availableEventCodeOptions}
        value={draft.eventCode}
        onChange={handleEventCodeChange}
        findings={findings}
        placeholder="Select a SAME event code"
        isSearchable
        isOptionDisabled={(option) => Boolean(option.disabledReason)}
        formatOptionLabel={formatEventCodeOption}
        required
      />

      <IpawsDeliveryChannelsField
        draft={draft}
        findings={findings}
        environment={environment}
        authorizedWeaHandlingOptions={authorizedWeaHandlingOptions}
        isWeaHandlingLoading={isWeaHandlingLoading}
        isWeaHandlingError={isWeaHandlingError}
        channelRules={channelRules}
        onDraftChange={onDraftChange}
      />

      <IpawsTextField
        controlId="ipaws-alert-event"
        fieldPath="event"
        label="Event"
        value={draft.event}
        onChange={onTextChange}
        findings={findings}
        placeholder="Describe the event type"
        required
      />

      <IpawsSelectField
        inputId="ipaws-alert-category"
        fieldPath="category"
        label="Category (select one or more) *"
        options={controlledOptions.category}
        value={draft.category}
        onChange={(options) => onDraftChange({ category: getOptionValues(options) })}
        findings={findings}
        placeholder="Select categories"
        isMulti
        isSearchable
        formatOptionLabel={formatCategoryOption}
        required
      />

      <IpawsSelectField
        inputId="ipaws-alert-response-type"
        fieldPath="responseType"
        label="Recommended Public Action"
        options={availableResponseTypeOptions}
        selectedOptions={recommendedPublicActionOptions}
        value={draft.responseType}
        onChange={handleResponseTypeChange}
        findings={findings}
        placeholder="Select a recommended public action"
        isMulti
        isSearchable
        isClearable
      />

      <div className="alert-creator-grid alert-creator-grid-compact">
        {priorityFields.map(([fieldPath, label, options]) => (
          <IpawsSelectField
            key={fieldPath}
            inputId={`ipaws-alert-${fieldPath}`}
            fieldPath={fieldPath}
            label={`${label} *`}
            options={options}
            selectedOptions={controlledOptions[fieldPath]}
            value={draft[fieldPath]}
            onChange={(option) => onDraftChange({ [fieldPath]: option?.value ?? '' })}
            findings={findings}
            placeholder={`${label} *`}
            required
            wrapperClassName="alert-creator-select flex-grow-full"
          />
        ))}
      </div>
    </fieldset>
  );
};

export default IpawsEventClassificationSection;
